import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { ApiService, BookingErrorCode, BookingValidation, CustomerInfo, InsightsService, LocationInfo, ObjectTypeInfo, PriceInfoModel, RentalInfo, TypeCategoryInfo, UIService, ResultDetailLevel, RentalMetaData, RentalObjectInfo, RentalStatus } from 't4core';
import { BookingModel } from '../../Models/BookingModel';
import { AppIntegrationService, Coordinates, AppSettingsService } from 't4-app-integration';
import { TypeGroup } from '../../Models/TypeGroup';
import { NavigationService } from '../NavigationService/navigation-service.service';

@Injectable({
  providedIn: 'root'
})
export class BookingService {
  private static readonly offerCreatedStorageKey = 'booking.offerCreated';
  private static readonly changeBookingBannerHiddenStorageKey = 'booking.changeBookingBannerHidden';
  private changeBookingRentalId: number = null;
  private changeBookingBannerHidden: boolean = false;

  // Booking model
  public Query: BookingQuery = new BookingQuery();

  // View model
  private locations: LocationInfo[] = [];
  private types: ObjectTypeInfo[] = [];
  private categories: TypeCategoryInfo[] = [];
  private priceInfo: any = {};
  private rentalMetaData = new RentalMetaData();

  private getLocationsIsRunning: boolean = false;
  // Get all available locations
  public async getLocations(): Promise<LocationInfo[]> {
    // If already cached, return them from memory
    if (this.locations.length > 0) return this.locations;

    // Wait for any previous run tom complete
    while (this.getLocationsIsRunning) await this.delay(100);

    // If already cached, return them from memory
    if (this.locations.length > 0) return this.locations;

    this.getLocationsIsRunning = true;

    // Retrieve from api
    this.locations = await this.Api.get<LocationInfo[]>("RentalLocation/App/GetLocations", { withCategories: true }, null, null, ResultDetailLevel.Slim);
    this.appSettings.setLocations(this.locations);

    // Return the data
    this.getLocationsIsRunning = false;

    return this.locations;
  }

  private searchedPlace: Coordinates;

  constructor(
    private appService: AppIntegrationService,
    private Api: ApiService,
    private UI: UIService,
    public appSettings: AppSettingsService,
    private insights: InsightsService) {
      // Has the module been loaded with the inStore-parameter?
    this.inStore = this.appSettings.getParam("inStore") == "true";
    this._offerCreated = localStorage.getItem(BookingService.offerCreatedStorageKey) == "true";
    this.changeBookingBannerHidden = localStorage.getItem(BookingService.changeBookingBannerHiddenStorageKey) == "true";
  }

  public startNew() {
    this.Query = new BookingQuery();
    this.offerCreated = false;
  }


  /******* Der alte shit *******/
  public preLoadedCategories: TypeCategoryInfo[];

  private bookingData: BookingModel = {
    booking: new RentalInfo(),
    timeSteps: "quarter",
    session: new Date().getTime(),
    existingBooking: null,
    objectCategories: this.categories,
  };

  private _offerCreated: boolean = false;
  public get offerCreated(): boolean {
    return this._offerCreated;
  }
  public set offerCreated(value: boolean) {
    this._offerCreated = value;
    localStorage.setItem(BookingService.offerCreatedStorageKey, value ? "true" : "false");
  }

  private get isBookingPaymentReturn(): boolean {
    return this.appSettings.getParam("action") != null
      && this.appSettings.getParam("flow") == "Booking"
      && this.appSettings.getParam("rentalId") != null;
  }

  public maxObjects: number = 5;
  public loading: boolean = false;
  public command: string;


  // Resets all values and starts a new session
  public async startSession(): Promise<boolean> {
    this.clearChangeBookingFlow();

    this.bookingData = {
      booking: new RentalInfo(),
      timeSteps: "quarter",
      session: new Date().getTime(),
      existingBooking: null,
      objectCategories: [],
    };

    this.maxObjects = 5;
    this.loading = false;
    this.command = null;
    if (!this.isBookingPaymentReturn) {
      this.offerCreated = false;
    }

    // Load initial data
    if (!this.locations || this.locations.length <= 0) {
      var success = await this.getLocations();
      if (!success) { return false; }
    }

    //if (!this.appSettings.getAnticipatedUserCountry()) {
      // Get coordinates to determine country
      this.appService.getCoordinates(false).then(coords => this.appSettings.registerLastKnownPosition(coords));
    //}
    

    if (this.appSettings.getParam("pickupLocationId")) {
      var loc = this.locations.find(x => (x.Id + "") == this.appSettings.getParam("pickupLocationId"));
      if (loc) {
        this.setLocations(loc, null);
      }
    }

    //If we are in the web view, to be sure we clear the user.
    if (this.appSettings.appType == "web") {
      this.appSettings.setUser(null);
    }

    return true;
  }

  public async treatPendingPaymentAsConfirmed(rentalId: number): Promise<boolean> {
    const result = await this.Api.post<any>("Rental/App/TreatPendingPaymentAsConfirmed", null, { rentalId: rentalId });
    console.log("treatPendingPaymentAsConfirmed result", result);
    const confirmed = result === true || result?.Success === true || result?.success === true;

    if (confirmed && this.bookingData.booking?.Id === rentalId) {
      this.bookingData.booking.Status = RentalStatus.Reserved;
    }

    return confirmed;
  }

  public startChangeBookingFlow(rentalId: number = null) {
    this.changeBookingRentalId = rentalId ?? this.changeBookingRentalId;

    if (this.changeBookingRentalId) {
      this.bookingData.existingBooking = this.changeBookingRentalId.toString();
    }
  }

  public showChangeBookingBanner(showBanner: boolean = true) {
    this.changeBookingBannerHidden = !showBanner;

    if (showBanner) {
      localStorage.removeItem(BookingService.changeBookingBannerHiddenStorageKey);
    }
    else {
      localStorage.setItem(BookingService.changeBookingBannerHiddenStorageKey, "true");
    }
  }

  public clearChangeBookingFlow() {
    this.changeBookingRentalId = null;
    this.bookingData.existingBooking = null;
    this.showChangeBookingBanner(true);
  }

  public isChangeBookingFlowActive(): boolean {
    return this.changeBookingRentalId != null || !!this.bookingData?.existingBooking;
  }

  public shouldShowChangeBookingBanner(): boolean {
    return this.isChangeBookingFlowActive() && !this.changeBookingBannerHidden;
  }


  // Returns the closest location with the Cords procivded.
  public findClosestLocation(currentLat: number, currentLon: number): LocationInfo | null {
    const filteredLocations = this.locations.filter(x => x.OpenForPickup);
    if (filteredLocations.length === 0) return null;

    return filteredLocations.reduce((closest, current) => {
      const closestDistance = this.calculateDistance(currentLat, currentLon, closest.TrailerPointLatitude, closest.TrailerPointLongitude);
      const currentDistance = this.calculateDistance(currentLat, currentLon, current.TrailerPointLatitude, current.TrailerPointLongitude);
      return currentDistance < closestDistance ? current : closest;
    });
  }


  public findSingleClosetLocationWithinDistance(currentCoords: Coordinates, atStationDistance: number, maxDistanceMeter: number): LocationInfo | null {
    if (this.locations.length === 0) return null;

    const maxKm = maxDistanceMeter / 1000;
    const atKm = atStationDistance / 1000;

    let closest: LocationInfo | null = null;
    let closestDistKm = Number.POSITIVE_INFINITY;
    let withinMaxCount = 0;
    let isAtAStation = false;

    for (const l of this.locations) {
      if (!l.OpenForPickup) continue;

      const dKm = this.calculateDistance(currentCoords.Latitude, currentCoords.Longitude, l.Latitude, l.Longitude); // km

      if (dKm <= atKm) isAtAStation = true;

      if (dKm <= maxKm) {
        withinMaxCount++;
        if (dKm < closestDistKm) {
          closestDistKm = dKm;
          closest = l;
        }
      }
    }

    // Nothing is close at all.
    if (withinMaxCount === 0) return null;

    // ambiguity rule: you're at a station and there is another station nearby
    if (isAtAStation && withinMaxCount > 1) return null;

    return closest; // the single closest within maxDistance
  }


  public getClosestLocations(searchLat: number, searchLon: number, locations: LocationInfo[], limit: number = 10): LocationInfo[] {
    return locations 
      .map(location => ({
        ...location,
        distance: this.calculateDistance(searchLat, searchLon, location.Latitude, location.Longitude),
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, limit);
  }

  public async getRandomRemoteLocation(currentCoords: Coordinates, minDistance: number): Promise<LocationInfo | null> {

   

    const qualifyingLocations = (await this.getLocations()).filter(location => {
      const distanceKm = this.calculateDistance(
        currentCoords.Latitude,
        currentCoords.Longitude,
        location.Latitude,
        location.Longitude
      );
      return distanceKm >= minDistance;
    });

    if (qualifyingLocations.length === 0) {
      return null;
    }

    const randomIndex = Math.floor(Math.random() * qualifyingLocations.length);
    return qualifyingLocations[randomIndex];
  }



  //Caluclates the distance - Haversine forumla
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const earthRadius = 6371; // Radius of the Earth in kilometers
    const dLat = this.degreesToRadians(lat2 - lat1);
    const dLon = this.degreesToRadians(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.degreesToRadians(lat1)) * Math.cos(this.degreesToRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = earthRadius * c; // Distance in kilometers
    return distance;
  }

  // Helper -- function to convert degrees to radians
  private degreesToRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  

  public async restartSession(rentalId: number): Promise<boolean> {
    var booking = await this.Api.get<RentalInfo>("/Rental/GetRentalInfo", { bookingId: rentalId });
    this.appSettings.localLocationId = booking.PickupLocation.Id;

    this.bookingData = {
      booking: booking,
      timeSteps: "quarter",
      session: new Date().getTime(),
      existingBooking: null,
      objectCategories: [],
    };

    this.maxObjects = 5;
    this.loading = false;
    this.command = null;

    // Load initial data
    if (!this.locations || this.locations.length <= 0) {
      var success = await this.getLocations();
      if (!success) { return false; }
    }

    return true;
  }

  /// Booking building methods
  async setPickupLocation(pickupLocation: LocationInfo) {
    this.bookingData.booking.PickupLocation = pickupLocation;
    this.bookingData.booking.ReturnLocation = pickupLocation;
    this.bookingData.booking.PickupLocationId = pickupLocation?.Id;
    this.appSettings.localLocationId = pickupLocation?.Id;
    if (this.bookingData.existingBooking) {
      this.bookingData.specificObject = null;
    }
    this.priceInfo = null;
  }
  async setReturnLocation(returnLocation: LocationInfo) {
    await this.setLocations(this.bookingData.booking.PickupLocation, returnLocation);
  }


  async setLocations(location: LocationInfo): Promise<void>;
  async setLocations(pickupLocation: LocationInfo | null, returnLocation: LocationInfo | null): Promise<void>;
  async setLocations(pickupLocationOrLocation: LocationInfo | null, returnLocation?: LocationInfo | null): Promise<void> {
    let pickupLocation = pickupLocationOrLocation;

    if (returnLocation === undefined) {
      returnLocation = pickupLocationOrLocation;
      if (pickupLocationOrLocation) {
        this.bookingData.booking.PickupLocation = pickupLocationOrLocation;
        this.bookingData.booking.ReturnLocation = pickupLocationOrLocation;
        if (this.bookingData.existingBooking) {
          this.bookingData.specificObject = null;
        }
      }
    }

    if (pickupLocation == null) pickupLocation = this.bookingData.booking.PickupLocation;
    if (returnLocation == null) returnLocation = this.bookingData.booking.ReturnLocation;

    this.bookingData.booking.PickupLocation = pickupLocation;
    this.bookingData.booking.ReturnLocation = returnLocation;
    this.bookingData.booking.PickupLocationId = pickupLocation.Id;
    this.bookingData.booking.ReturnLocationId = returnLocation?.Id;

    this.appSettings.localLocationId = pickupLocation.Id;

    this.priceInfo = null;

    if (pickupLocation) {
      var selectedLocation = await this.Api.get<LocationInfo>("RentalLocation/App/GetLocationInfo", { locationId: pickupLocation.Id }, null, null, ResultDetailLevel.Full);
      if (selectedLocation) {
        var distance = await this.appService.getDistanceFromUser(selectedLocation.TrailerPointLatitude, selectedLocation.TrailerPointLongitude) * 1000;
        this.bookingData.atLocation = (distance < selectedLocation.TrailerPointRadius) && !this.bookingData.booking?.Id;
      }
      else {
        var distance = await this.appService.getDistanceFromUser(pickupLocation.TrailerPointLatitude, pickupLocation.TrailerPointLongitude) * 1000;

        this.bookingData.atLocation = (distance < pickupLocation.TrailerPointRadius) && !this.bookingData.booking?.Id;
      }
    }
  }

  setCategory(objectCategory: TypeCategoryInfo) {
    this.bookingData.objectCategory = objectCategory;
  }

  setRentalPeriod(pickupTime: moment.Moment, returnTime: moment.Moment) {
    this.bookingData.pickupTime = pickupTime;
    this.bookingData.returnTime = returnTime;
  }
  /// Booking building methods

  /// Helper methods

  /// Load price list base information from the server
  public async getPriceInfo(): Promise<PriceInfoModel> {
    if (!this.priceInfo || Object.keys(this.priceInfo).length === 0) {
      this.priceInfo = await this.Api.get<any>("Price/GetPriceInfo", { pickupTime: new Date().toISOString(), objectType: null, webBooking: true });
    }

    return this.priceInfo;
  }

  /// Helper methods

  clearObjectType() {
    this.bookingData.booking.PrimaryType = null;
    this.bookingData.booking.PrimaryObjectTypeId = null;
  }

  getBookingState() {
    return this.bookingData;
  }

  // Get, and cache, types
  public async getType(id: number): Promise<ObjectTypeInfo> {
    var t = this.types.find(x => x.Id == id);
    if (t) return t;

    t = await this.Api.get<ObjectTypeInfo>("/RentalObject/GetTypeInfo", { objectType: id });

    this.types.push(t);
    return t;
  }

  public async getLocation(id: number): Promise<LocationInfo> {
    var loc = this.locations.find(x => x.Id == id);
    if (loc) return loc;

    await this.getLocations();

    var loc = this.locations.find(x => x.Id == id);
    return loc;
  }

  private delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}


  public async isCustomerAtLocation(): Promise<boolean> {
    // Never when changing booking
    if (this.bookingData.booking?.Id) {
      this.bookingData.atLocation = false;
      return true;
    }

    // Are there locations?
    if (this.locations && this.locations.length > 0) {

      // Calculate distance and check if user is within trailer point
      var distance = await this.appService.getDistanceFromUser(this.locations[0].TrailerPointLatitude, this.locations[0].TrailerPointLongitude) * 1000;
      this.bookingData.atLocation = distance < this.locations[0].TrailerPointRadius + 1000 && !this.bookingData.booking?.Id;

      // Is the demo setting set ? The override
      if (this.appSettings.userId && localStorage.getItem('atLocation'))
        this.bookingData.atLocation = localStorage.getItem('atLocation') == "true" ? true : this.bookingData.atLocation;
        
      // Select the closest location
      if (this.bookingData.atLocation) {
        this.bookingData.booking.PickupLocation = this.locations[0];
      }

      return this.bookingData.atLocation;
    }

    return false;
  }

  public saveBookingState(booking: RentalInfo) {
    for (let data in booking) {
      if (booking[data] !== undefined && this.bookingData.booking[data] != booking[data]) {
        this.bookingData.booking[data] = booking[data];
      }
    }

    if (this.bookingData.booking.PickupLocation && !this.bookingData.booking.PickupLocationId) {
      this.bookingData.booking.PickupLocationId = this.bookingData.booking.PickupLocation.Id;
    }

    if (this.bookingData.booking.ReturnLocation && !this.bookingData.booking.ReturnLocationId) {
      this.bookingData.booking.ReturnLocationId = this.bookingData.booking.ReturnLocation.Id;
    }
  }

  public saveState(newState: BookingModel) {
    this.saveBookingState(newState.booking);

    for (let data in newState) {
      //Check if properties of booking has changed
      if (data == "booking") continue;
      //Check if other properties of the bookingState has changed
      else {
        if (this.bookingData[data] != newState[data]) {
          this.bookingData[data] = newState[data];
        }
      }
    }

    if (this.bookingData.booking.PickupLocation) {
      this.appSettings.localLocationId = this.bookingData.booking.PickupLocation.Id;
    }

    this.bookingData.objectCategories = newState.objectCategories;
    this.bookingData.freshLoad = false;
  }

  public inStore: boolean = false;

  public async isWebBooking(): Promise<boolean> {

    if (this.inStore) return false;

    return true;
    var thirtyMinsFromNow = moment().add(30, 'minutes');
    var webBooking = !this.bookingData.atLocation;

    if (!webBooking && this.bookingData.booking.PickupTime >= thirtyMinsFromNow) {
      webBooking = true;
    }

    return webBooking;

  }

  public async saveCustomer(isOneWay: boolean = false): Promise<CustomerInfo> {
    this.Query.Customer = await this.Api.post<CustomerInfo>("/Rental/SaveCustomer", this.Query.Customer, { isOneWay: isOneWay });
    return this.Query.Customer;
  }

  public async saveBooking(webBooking: boolean = true, useSavedCard: boolean = true): Promise<any> {

    this.rentalMetaData.Source = this.appSettings.appType;
    this.rentalMetaData.Theme = this.appSettings.settings.theme;
    this.rentalMetaData.Language = this.appSettings.language;
    this.rentalMetaData.Context = this.appSettings.contextId;
    this.rentalMetaData.SubContext = this.appSettings.getParam("subcontext");
    if (document.referrer) {
      this.rentalMetaData.Referrer = new URL(document.referrer).hostname;
    }
    

    //await this.saveCustomer(this.bookingData.booking.IsOneWay);

    // Validate input

    // Is pickup time in the past? Can we assume that the booking process has taken more time than usual?
    // Then move pickup time and log the change
    if (this.Query.PickupTime < new Date(new Date().getTime() - (15 * 60 * 1000)) && this.Query.PickupTime > new Date(new Date().getTime() - (35 * 60 * 1000))) {
      this.insights.trackTrace("Correcting pickup time!", {
        current: JSON.stringify(this.Query.PickupTime)
      });

      this.Query.PickupTime = new Date();
    }

    console.log("booking service booking data", this.bookingData);
    //Customer confirm price changes if updating existing booking
    if (this.bookingData.existingBooking) {
      var existingBooking = await this.Api.get<RentalInfo>("/Rental/GetRentalInfo", { bookingId: this.bookingData.existingBooking });
      if (this.bookingData.booking.Price.DiscountedTotalPrice > existingBooking.Price.DiscountedTotalPrice) {
        //Add code to confirm changes here if necessary in the future
        var confirm = true;

        if (!confirm) {
          return { CustomerDenied: true };
        }
      }

    }
    //If new booking
    if (!this.bookingData.existingBooking) {

      var webBooking = await this.isWebBooking();

      //Get customer current location as Coordinates.
      var currentCoordinates = await this.appService.getCoordinates();
    
      //var token = this.UI.beginLoading("Intellitrailer.Booking.Payment.msgLoadingOptionsCreate", "Creating booking", null);
      var result = await this.Api.post<BookingValidation>("Rental/CreateBooking", this.Query.Customer,
        {
          pickupTime: this.bookingData.booking.PickupTime.toISOString(),
          returnTime: this.bookingData.booking.ReturnTime.toISOString(),
          pickupLocation: this.bookingData.booking.PickupLocation.Id,
          returnLocation: this.bookingData.booking.ReturnLocation ? this.bookingData.booking.ReturnLocation.Id : this.bookingData.booking.PickupLocation.Id,
          typeId: this.bookingData.booking.PrimaryObjectTypeId,
          discountId: this.bookingData.booking.DiscountId,
          notes: this.bookingData.booking.Note,
          addons: this.bookingData.booking.AddedProducts,
          //Online booking or not
          webBooking: webBooking,
          //Customer current coordinated when creating the booking.
          bookingLat: currentCoordinates ? currentCoordinates.Latitude : null,
          bookingLng: currentCoordinates ? currentCoordinates.Longitude : null,
          rentalMetaData: JSON.stringify(this.rentalMetaData),
          useCurrentContext: this.appSettings.contextId == "KanLeiesUnmanned_API" ? true : false,
          specificObjectId: this.bookingData.specificObject?.Id,
          travelRange: this.bookingData.booking.TravelRange
        });
      if (result.AggregatedResult == BookingErrorCode.Valid) {
        this.bookingData.booking.Id = result.BookingId;
        this.bookingData.booking = await this.Api.get<RentalInfo>("/Rental/GetRentalInfo", { bookingId: result.BookingId });
        this.offerCreated = result.OfferCreated ?? (result as any).offerCreated ?? false;
      }

      
      //this.UI.loaderCompleted(token);

      //if (result.AggregatedResult == BookingErrorCode.Valid) {
      //  await this.Api.put<BookingValidation>("/Rental/SetAddons", null,
      //    {
      //      rentalId: this.bookingData.booking.Id,
      //      products: this.bookingData.booking.AddedProducts,
      //    }
      //  );
      //}

      return result;
    }
    //If existing booking
    else {
      var token = this.UI.beginLoading("Intellitrailer.Booking.Payment.msgLoadingOptionsUpdate", "Updating booking", null);
      var result: BookingValidation = await this.Api.put<BookingValidation>("Rental/ChangeBooking", null, {
        rentalId: +this.bookingData.existingBooking,
        pickupTime: this.bookingData.booking.PickupTime.toISOString(),
        returnTime: this.bookingData.booking.ReturnTime.toISOString(),
        pickupLocation: this.bookingData.booking.PickupLocation.Id,
        returnLocation: this.bookingData.booking.PickupLocation.Id,
        typeId: this.bookingData.booking.PrimaryObjectTypeId,
        discountId: this.bookingData.booking.DiscountId,
        notes: this.bookingData.booking.Note,
        addons: this.bookingData.booking.AddedProducts,
        //Online booking or not
        webBooking: webBooking,
        //Customer current coordinated when creating the booking.
        bookingLat: currentCoordinates ? currentCoordinates.Latitude : null,
        bookingLng: currentCoordinates ? currentCoordinates.Longitude : null,
        rentalMetaData: JSON.stringify(this.rentalMetaData),
        specificObjectId: this.bookingData.specificObject?.Id,
        travelRange: this.bookingData.booking.TravelRange
      });
      this.UI.loaderCompleted(token);

      if (result.AggregatedResult == BookingErrorCode.Valid) {
        await this.Api.put<BookingValidation>("/Rental/SetAddons", null,
          {
            rentalId: +this.bookingData.existingBooking,
            products: this.bookingData.booking.AddedProducts,
          }
        );
        console.log("updated addons", this.bookingData);
        this.bookingData.booking.Id = +this.bookingData.existingBooking;
        this.bookingData.booking = await this.Api.get<RentalInfo>("/Rental/GetRentalInfo", { bookingId: this.bookingData.existingBooking });
      }

      return result;
    }
  }

  public async getOptions(location: LocationInfo, ignoreTimeLimit: boolean = false): Promise<TypeGroup[]> {
    let typesInCategory: ObjectTypeInfo[] = await this.Api.get<ObjectTypeInfo[]>("RentalObject/App/GetAvailableTypes",
        {
          locationId: location.Id,
          categoryId: null,
          pickupTime:
            (!this.bookingData.booking.PickupTime && this.bookingData.atLocation ? moment().toISOString() :
              this.bookingData.booking.PickupTime.clone().toISOString()),
          returnTime: this.bookingData.booking.ReturnTime.clone().toISOString(),
        bookingId: this.bookingData.booking && this.bookingData.booking.Id ? this.bookingData.booking.Id : null
      }, null, null, ResultDetailLevel.Slim);

      let group: TypeGroup = { Category: null, Options: await this.filterAndConvertToBookings(typesInCategory, location) };
      let bookingOptions: TypeGroup[] = [];
      if (this.bookingData.objectCategories && this.bookingData.objectCategories.length > 0) {
        bookingOptions.unshift(group);
      }
      else if (group.Options.length > 0) {
        bookingOptions.push(group);
      }

      this.loading = false;
      return bookingOptions;
  }

  public async filterAndConvertToBookings(availableTypes: ObjectTypeInfo[], location: LocationInfo = null, pickupTime: moment.Moment = null, returnTime: moment.Moment = null): Promise<RentalInfo[]> {

    var bookingOptions: RentalInfo[] = [];
    availableTypes.forEach((type) => {
      var bookingOption: RentalInfo = new RentalInfo();
      bookingOption.PickupLocation = location ? location : this.bookingData.booking.PickupLocation;
      bookingOption.PickupLocationId = location ? location.Id : this.bookingData.booking.PickupLocation.Id;
      bookingOption.PickupTime = pickupTime ? pickupTime : this.bookingData.booking.PickupTime;
      bookingOption.ReturnTime = returnTime ? returnTime : this.bookingData.booking.ReturnTime;
      bookingOption.PrimaryType = type;
      bookingOption.PrimaryObjectTypeId = type.Id;
      bookingOption.DiscountId = this.bookingData.booking.DiscountId;

      if (!bookingOptions.find(x => x.PrimaryObjectTypeId == type.Id)) bookingOptions.push(bookingOption);
    });

    return bookingOptions;
  }


  public async getFamiliesAtLocation(location: LocationInfo) {
    var categories: TypeCategoryInfo[] = await this.Api.get<TypeCategoryInfo[]>("RentalObject/GetFamiliesAtLocation", { locationId: location.Id })
    return categories;
  }


  public async getCategories(): Promise<TypeCategoryInfo[]> {
    if (this.preLoadedCategories) return this.preLoadedCategories;
    else {
      //var token = this.UI.beginLoading("Intellitrailer.Booking.Preloader.General", "Preparing view", null);
      this.preLoadedCategories = await this.Api.get<TypeCategoryInfo[]>("/RentalObject/App/GetObjectFamilies", null, null, null, ResultDetailLevel.Slim);
      //this.UI.loaderCompleted(token);

      this.preLoadedCategories.sort((x, y) => x.SortOrder < y.SortOrder ? -1 : 1);

      return this.preLoadedCategories;
    }
  }

  public fixDistance(distance: number): string {
    if (distance < 1) {
      var distanceMeters = distance * 1000;
      return +distanceMeters.toFixed(0) + " m";
    }

    return +distance.toFixed(1) + " km";
  }

  public async redirectToPayment(rentalId: number, returnPath: String) {
    let paymentURI = await this.Api.get("Rental/GetPaymentURI", { rentalId: rentalId });
    let returnUri = `/${returnPath}`;
    returnUri = returnUri.replace("%2F", "/").replace("%2F", "/");
    returnUri = returnUri.replace("//", "/");

    var base = '';
    if (window['baseUri']) base = window['baseUri'];
    returnUri = window.location.origin + base + returnUri;

    let scheme = this.appSettings.appScheme ?? "";

    let paymentUrl = paymentURI + `?returnuri=${returnUri}&appScheme=${scheme}`;
    document.location.href = paymentUrl;
  }


  public updateSearchedCoordinates(coords: Coordinates) {
    if (coords) this.searchedPlace = coords;
  }

  public getSearchedCoordinates() {
    return this.searchedPlace;
  }
  public setSpecificObject(object: RentalObjectInfo) {
    if (!this.bookingData.booking || !object) return;
    this.bookingData.specificObject = object;
  }
  public clearSpecificObject() {
    if (!this.bookingData.booking) return;
    this.bookingData.specificObject = null;
  }
  public getSpecificObject() {
    return this.bookingData.specificObject;
  }
}

export class BookingQuery {
  public PickupTime?: Date;
  public ReturnTime?: Date;

  public PickupLocationId?: number;
  public ReturnLocationId?: number;

  public ObjectTypeId?: number;

  public Customer: CustomerInfo = new CustomerInfo();
}

